/**
 * Privy JS SDK Core — thin Vue wrapper.
 *
 * Since Privy does not publish a Vue-specific SDK, this module wraps
 * @privy-io/js-sdk-core to provide a singleton Privy client instance
 * and utility helpers for the Vue composables.
 */

import { config } from '@/config'
import type { AuthenticatedUser, User as PrivyUser } from '@privy-io/api-types'
import Privy, {
    getEntropyDetailsFromUser,
    getUserEmbeddedSolanaWallet,
    LocalStorage,
    type ConnectedStandardSolanaWallet,
} from '@privy-io/js-sdk-core'

/** Authenticated user result from Privy custom auth (without session_update_action) */
export type PrivyAuthResult = Omit<AuthenticatedUser, 'session_update_action'>

export { getEntropyDetailsFromUser }
export type { ConnectedStandardSolanaWallet, PrivyUser }

let privyInstance: Privy | null = null
let iframeLoaded = false

/**
 * Returns the singleton Privy client, creating it on first call.
 */
export function getPrivyClient(): Privy {
    if (!privyInstance) {
        privyInstance = new Privy({
            appId: config.privyAppId,
            storage: new LocalStorage(),
        })
    }
    return privyInstance
}

/**
 * Load the embedded wallet iframe so that createSolana() and signing work.
 * This must be called after initialize() and after user is authenticated.
 */
export async function loadEmbeddedWalletIframe(): Promise<void> {
    if (iframeLoaded) return
    const client = getPrivyClient()
    const url = client.embeddedWallet.getURL()

    return new Promise((resolve, reject) => {
        const iframe = document.createElement('iframe')
        iframe.src = url
        iframe.style.display = 'none'
        iframe.id = 'privy-embedded-wallet-iframe'
        iframe.allow = 'publickey-credentials-get *; clipboard-write'

        const timeout = setTimeout(() => {
            reject(new Error('Embedded wallet iframe load timeout'))
        }, 15_000)

        iframe.onload = () => {
            clearTimeout(timeout)
            // Set up the message poster for the embedded wallet proxy
            client.setMessagePoster({
                postMessage: (message: string, _origin: string) => {
                    iframe.contentWindow?.postMessage(message, '*')
                },
                reload: () => {
                    iframe.src = url
                },
            })

            // Listen for messages from the iframe
            const handler = (event: MessageEvent) => {
                if (event.source !== iframe.contentWindow) return
                try {
                    const data = typeof event.data === 'string'
                        ? JSON.parse(event.data)
                        : event.data
                    if (data.event?.startsWith('privy:')) {
                        client.embeddedWallet.onMessage(data)
                    }
                } catch {
                    // Ignore non-Privy messages
                }
            }
            window.addEventListener('message', handler)
            iframeLoaded = true
            resolve()
        }

        iframe.onerror = () => {
            clearTimeout(timeout)
            reject(new Error('Failed to load embedded wallet iframe'))
        }

        document.body.appendChild(iframe)
    })
}

let initPromise: Promise<void> | null = null

/**
 * Initialize the Privy SDK. Safe to call multiple times — subsequent
 * calls return the same promise. Called from main.ts at startup and
 * from usePrivyAuth before any auth operations.
 */
export async function initializePrivy(): Promise<void> {
    if (!initPromise) {
        const client = getPrivyClient()
        initPromise = client.initialize()
    }
    return initPromise
}

/**
 * Authenticate with Privy using a custom JWT (Supabase session token).
 * This syncs the Supabase-authenticated user into Privy so that an
 * embedded wallet can be provisioned.
 */
export async function loginWithCustomToken(
    token: string
): Promise<PrivyAuthResult> {
    const client = getPrivyClient()
    return client.auth.customProvider.syncWithToken(token)
}

/**
 * Create a Solana embedded wallet for the authenticated user.
 * Call this after loginWithCustomToken() since custom auth
 * does not auto-create wallets.
 */
export async function createSolanaWallet(): Promise<PrivyAuthResult | null> {
    const client = getPrivyClient()
    try {
        // Ensure the embedded wallet iframe is loaded
        await loadEmbeddedWalletIframe()
        const result = await client.embeddedWallet.createSolana()
        return result
    } catch (err) {
        // If wallet already exists, that's fine
        const message = err instanceof Error ? err.message : String(err)
        if (message.includes('already') || message.includes('exists')) {
            return null
        }
        throw err
    }
}

/**
 * Get the current Privy access token, or null if not authenticated.
 */
export async function getPrivyAccessToken(): Promise<string | null> {
    const client = getPrivyClient()
    return client.getAccessToken()
}

/**
 * Extract the Solana embedded wallet account from a Privy user object.
 * Returns the LinkedAccountSolanaEmbeddedWallet entry if present.
 */
export function getSolanaEmbeddedWallet(user: PrivyUser) {
    return getUserEmbeddedSolanaWallet(user)
}
